// @ts-nocheck
import { ApiParameter, get, post } from "@insite/client-framework/Services/ApiService";
import { CampaignItem } from "@store/Library/Widgets/FlyoutCampaignNotification";

// API parameter interfaces
export interface GetCampaignStatusParameter extends ApiParameter {
    userId: string;
    campaignId: string;
}

// API response interfaces matching the documentation
export interface CampaignStatusResponse {
    campaignId: string;
    campaignStatus: "Unread" | "Read";
}

export interface UpdateCampaignStatusRequest {
    userId: string;
    campaignId: string;
    status: "Read" | "Unread";
}

export interface UpdateCampaignStatusResponse {
    campaignId: string;
    status: "Read" | "Unread";
}

const API_BASE_URL = "/api/v1/custom/flyoutCampaign";

// Session storage key for tracking campaigns shown in current session
export const LOCAL_STORAGE_KEY = "flyoutCampaigns_shownInSession";

/**
 * Get campaigns that have been shown in the current session
 */
const getShownCampaignsInSession = (): string | null => {
    try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error reading session storage:", error);
        return null;
    }
};

/**
 * Check if a campaign has been shown in the current session
 */
const isACampaignShownInSession = (): boolean => {
    return !!getShownCampaignsInSession();
};

/**
 * Mark a campaign as shown in the current session
 */
export const markCampaignAsShownInSession = (): void => {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(true));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating session storage:", error);
    }
};
/**
 * Get campaign status for a specific user and campaign
 */
export const getCampaignStatus = async (userId: string, campaignId: string): Promise<CampaignStatusResponse> => {
    try {
        const parameter: GetCampaignStatusParameter = {
            userId,
            campaignId,
        };

        return await get<CampaignStatusResponse>(`${API_BASE_URL}/status`, parameter);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error getting campaign status:", error);
        throw error;
    }
};

/**
 * Update campaign status for a specific user and campaign
 */
export const updateCampaignStatus = async (
    userId: string,
    campaignId: string,
    status: "Read" | "Unread",
): Promise<UpdateCampaignStatusResponse> => {
    try {
        const requestBody: UpdateCampaignStatusRequest = {
            userId,
            campaignId,
            status,
        };

        return await post<UpdateCampaignStatusResponse>(`${API_BASE_URL}/update`, requestBody);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating campaign status:", error);
        throw error;
    }
};

/**
 * Check if a campaign should be shown to a user
 */
export const shouldShowCampaign = async (userId: string, campaignId: string): Promise<boolean> => {
    try {
        const statusResponse = await getCampaignStatus(userId, campaignId);
        return statusResponse.campaignStatus === "Unread";
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error checking if campaign should be shown:", error);
        // Default to not showing campaign if API fails
        return false;
    }
};

/**
 * Mark a campaign as read (user dismissed it)
 */
export const markCampaignAsRead = async (userId: string, campaignId: string): Promise<void> => {
    try {
        await updateCampaignStatus(userId, campaignId, "Read");
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error marking campaign as read:", error);
        throw error;
    }
};

/**
 * Mark a campaign as unread
 */
export const markCampaignAsUnread = async (userId: string, campaignId: string): Promise<void> => {
    try {
        await updateCampaignStatus(userId, campaignId, "Unread");
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error marking campaign as read:", error);
        throw error;
    }
};

/**
 * Check if a campaign is eligible to be shown based on various criteria
 */
export const isCampaignEligible = async (campaign: CampaignItem, userId: string): Promise<boolean> => {
    const now = new Date();

    // Check if campaign is active
    if (!campaign.fields.isActive) {
        return false;
    }

    // Check date range
    const startDate = new Date(campaign.fields.startDate);
    const endDate = new Date(campaign.fields.endDate);
    if (now < startDate || now > endDate) {
        return false;
    }

    // Check if campaign has already been shown in this session
    if (isACampaignShownInSession()) {
        return false;
    }

    // Check campaign status via API
    try {
        return await shouldShowCampaign(userId, campaign.fields.campaignId);
    } catch (error) {
        // If API fails, default to not showing campaign
        return false;
    }
};

/**
 * Get all eligible campaigns for a user, filtered and sorted by start date
 */
export const getEligibleCampaigns = async <
    T extends CampaignItem & {
        [key: string]: any; // Allow additional properties
    },
>(
    campaigns: T[],
    userId: string,
): Promise<T | null> => {
    try {
        // Check eligibility for all campaigns
        const eligibilityPromises = campaigns.map(async campaign => {
            const isEligible = await isCampaignEligible(campaign, userId);
            return { campaign, isEligible };
        });

        const eligibilityResults = await Promise.all(eligibilityPromises);
        const eligibleCampaigns = eligibilityResults.filter(result => result.isEligible).map(result => result.campaign);

        // Sort by start date (earliest first)
        return eligibleCampaigns.sort(
            (a, b) => new Date(a.fields.startDate).getTime() - new Date(b.fields.startDate).getTime(),
        )[0];
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error getting eligible campaigns:", error);
        return null;
    }
};
